import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './StudentDashboard.css';

interface StudentNote {
  id: string;
  title: string;
  content: string;
  course_name: string;
  session_date: string;
  tags: string[];
  is_private: boolean;
  created_at: string;
  updated_at?: string;
}

interface Assignment {
  id: string;
  title: string;
  description: string;
  due_date: string;
  course: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
}

interface Progress {
  course: string;
  overall_progress: number;
  skills_learned: string[];
  next_goals: string[];
  last_updated: string;
}

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'assignments' | 'notes' | 'progress' | 'courses'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [notes, setNotes] = useState<StudentNote[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [progress, setProgress] = useState<Progress[]>([]);
  const [courses, setCourses] = useState<string[]>([]);
  
  // Note form states
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    course_name: '',
    session_date: '',
    tags: [] as string[],
    is_private: false
  });
  const [editingNote, setEditingNote] = useState<StudentNote | null>(null);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching student dashboard data from database...');
      
      // Get student ID from user context
      const studentId = user?.username || 'student1'; // Fallback for testing
      
      // Fetch all dashboard data from API
      const response = await fetch(`/api/student-dashboard/${studentId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      
      const data = await response.json();
      
      // Set all the data from the API response
      setAssignments(data.assignments || []);
      setProgress(data.progress || []);
      setCourses(data.courses || []);
      setNotes(data.notes || []);
      
      console.log('✅ Student dashboard data loaded successfully from database');
      
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      // Fetch notes from API
      const studentId = user?.username || 'student1';
      const response = await fetch(`/api/student-notes/student/${studentId}`);
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
      } else {
        console.warn('Failed to fetch notes from API, using empty array');
        setNotes([]);
      }
    } catch (err: any) {
      console.error('Error fetching notes:', err);
      setNotes([]);
    }
  };

  const handleCreateNote = async () => {
    try {
      // Create note via API
      const response = await fetch('/api/student-notes/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newNote,
          student_id: user?.username || 'student1'
        })
      });
      
      if (response.ok) {
        const createdNote = await response.json();
        setNotes(prev => [createdNote, ...prev]);
        setShowNoteForm(false);
        setNewNote({
          title: '',
          content: '',
          course_name: '',
          session_date: '',
          tags: [],
          is_private: false
        });
      } else {
        throw new Error('Failed to create note');
      }
      
    } catch (err: any) {
      console.error('Error creating note:', err);
      // Fallback to local state update
      const newNoteData: StudentNote = {
        id: Date.now().toString(),
        title: newNote.title,
        content: newNote.content,
        course_name: newNote.course_name,
        session_date: newNote.session_date,
        tags: newNote.tags,
        is_private: newNote.is_private,
        created_at: new Date().toISOString()
      };
      
      setNotes(prev => [newNoteData, ...prev]);
      setShowNoteForm(false);
      setNewNote({
        title: '',
        content: '',
        course_name: '',
        session_date: '',
        tags: [],
        is_private: false
      });
    }
  };

  const handleUpdateNote = async (noteId: string, updatedData: Partial<StudentNote>) => {
    try {
      // Update note via API
      const response = await fetch(`/api/student-notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });
      
      if (response.ok) {
        const updatedNote = await response.json();
        setNotes(prev => prev.map(note => 
          note.id === noteId ? updatedNote : note
        ));
        setEditingNote(null);
      } else {
        throw new Error('Failed to update note');
      }
    } catch (err: any) {
      console.error('Error updating note:', err);
      // Fallback to local state update
      setNotes(prev => prev.map(note => 
        note.id === noteId ? { ...note, ...updatedData } : note
      ));
      setEditingNote(null);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      // Delete note via API
      const response = await fetch(`/api/student-notes/${noteId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setNotes(prev => prev.filter(note => note.id !== noteId));
      } else {
        throw new Error('Failed to delete note');
      }
    } catch (err: any) {
      console.error('Error deleting note:', err);
      // Fallback to local state update
      setNotes(prev => prev.filter(note => note.id !== noteId));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'in_progress': return '#ffc107';
      case 'pending': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="student-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-dashboard">
        <div className="error-container">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      <div className="dashboard-header">
        <h1>🎓 Student Dashboard</h1>
        <p>Welcome back, {user?.email}</p>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          📝 Assignments
        </button>
        <button 
          className={`nav-tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          📚 Notes
        </button>
        <button 
          className={`nav-tab ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          📈 Progress
        </button>
        <button 
          className={`nav-tab ${activeTab === 'courses' ? 'active' : ''}`}
          onClick={() => setActiveTab('courses')}
        >
          🏫 Courses
        </button>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <h2>📊 Your Learning Overview</h2>
            
            <div className="stats-grid">
              <div className="stat-card">
                <h3>📝 Active Assignments</h3>
                <div className="stat-number">{assignments.filter(a => a.status !== 'completed').length}</div>
                <p>Keep up the great work!</p>
              </div>
              
              <div className="stat-card">
                <h3>📚 Notes Created</h3>
                <div className="stat-number">{notes.length}</div>
                <p>Great job documenting your learning!</p>
              </div>
              
              <div className="stat-card">
                <h3>🏫 Courses</h3>
                <div className="stat-number">{courses.length}</div>
                <p>You're enrolled in {courses.length} courses</p>
              </div>
              
              <div className="stat-card">
                <h3>📈 Average Progress</h3>
                <div className="stat-number">
                  {Math.round(progress.reduce((sum, p) => sum + p.overall_progress, 0) / progress.length)}%
                </div>
                <p>Excellent progress!</p>
              </div>
            </div>

            <div className="recent-activity">
              <h3>🔥 Recent Activity</h3>
              <div className="activity-list">
                {notes.slice(0, 3).map(note => (
                  <div key={note.id} className="activity-item">
                    <div className="activity-icon">📚</div>
                    <div className="activity-content">
                      <p><strong>Note:</strong> {note.title}</p>
                      <p className="activity-meta">{note.course_name} • {new Date(note.session_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assignments' && (
          <div className="assignments-tab">
            <h2>📝 Your Assignments</h2>
            
            <div className="assignments-list">
              {assignments.map(assignment => (
                <div key={assignment.id} className="assignment-card">
                  <div className="assignment-header">
                    <h3>{assignment.title}</h3>
                    <div className="assignment-badges">
                      <span 
                        className="status-badge" 
                        style={{ backgroundColor: getStatusColor(assignment.status) }}
                      >
                        {assignment.status.replace('_', ' ')}
                      </span>
                      <span 
                        className="priority-badge" 
                        style={{ backgroundColor: getPriorityColor(assignment.priority) }}
                      >
                        {assignment.priority} priority
                      </span>
                    </div>
                  </div>
                  
                  <p className="assignment-description">{assignment.description}</p>
                  
                  <div className="assignment-meta">
                    <p><strong>Course:</strong> {assignment.course}</p>
                    <p><strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleDateString()}</p>
                  </div>
                  
                  <div className="assignment-actions">
                    <button className="btn btn-primary">View Details</button>
                    <button className="btn btn-secondary">Mark Complete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="notes-tab">
            <div className="notes-header">
              <h2>📚 Your Session Notes</h2>
              <button 
                className="btn btn-primary"
                onClick={() => setShowNoteForm(true)}
              >
                ➕ Add New Note
              </button>
            </div>

            {/* Course Filter */}
            <div className="course-filter">
              <label htmlFor="course-select">Filter by Course:</label>
              <select 
                id="course-select"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
              >
                <option value="">All Courses</option>
                {courses.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>

            {/* Notes List */}
            <div className="notes-list">
              {notes
                .filter(note => !selectedCourse || note.course_name === selectedCourse)
                .map(note => (
                <div key={note.id} className="note-card">
                  <div className="note-header">
                    <h3>{note.title}</h3>
                    <div className="note-actions">
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => setEditingNote(note)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  
                  <div className="note-meta">
                    <span className="course-badge">{note.course_name}</span>
                    <span className="date-badge">{new Date(note.session_date).toLocaleDateString()}</span>
                    {note.is_private && <span className="private-badge">🔒 Private</span>}
                  </div>
                  
                  <p className="note-content">{note.content}</p>
                  
                  {note.tags.length > 0 && (
                    <div className="note-tags">
                      {note.tags.map(tag => (
                        <span key={tag} className="tag">#{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="progress-tab">
            <h2>📈 Your Learning Progress</h2>
            
            <div className="progress-list">
              {progress.map((prog, index) => (
                <div key={index} className="progress-card">
                  <div className="progress-header">
                    <h3>{prog.course}</h3>
                    <div className="progress-percentage">{prog.overall_progress}%</div>
                  </div>
                  
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${prog.overall_progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="progress-details">
                    <div className="skills-section">
                      <h4>🎯 Skills Learned</h4>
                      <ul>
                        {prog.skills_learned.map(skill => (
                          <li key={skill}>{skill}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="goals-section">
                      <h4>🎯 Next Goals</h4>
                      <ul>
                        {prog.next_goals.map(goal => (
                          <li key={goal}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <p className="last-updated">Last updated: {new Date(prog.last_updated).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="courses-tab">
            <h2>🏫 Your Courses</h2>
            
            <div className="courses-list">
              {courses.map(course => (
                <div key={course} className="course-card">
                  <h3>{course}</h3>
                  <p>Continue your learning journey in this exciting course!</p>
                  <div className="course-actions">
                    <button className="btn btn-primary">View Details</button>
                    <button className="btn btn-secondary">View Notes</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Note Form Modal */}
      {showNoteForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📚 Add New Note</h3>
              <button 
                className="modal-close"
                onClick={() => setShowNoteForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="note-title">Title</label>
                <input
                  id="note-title"
                  type="text"
                  value={newNote.title}
                  onChange={(e) => setNewNote({...newNote, title: e.target.value})}
                  placeholder="Enter note title"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="note-course">Course</label>
                <select
                  id="note-course"
                  value={newNote.course_name}
                  onChange={(e) => setNewNote({...newNote, course_name: e.target.value})}
                >
                  <option value="">Select a course</option>
                  {courses.map(course => (
                    <option key={course} value={course}>{course}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="note-date">Session Date</label>
                <input
                  id="note-date"
                  type="datetime-local"
                  value={newNote.session_date}
                  onChange={(e) => setNewNote({...newNote, session_date: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="note-content">Content</label>
                <textarea
                  id="note-content"
                  value={newNote.content}
                  onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                  placeholder="Write your notes here..."
                  rows={6}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="note-tags">Tags (comma-separated)</label>
                <input
                  id="note-tags"
                  type="text"
                  value={newNote.tags.join(', ')}
                  onChange={(e) => setNewNote({...newNote, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})}
                  placeholder="e.g., arduino, programming, robotics"
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newNote.is_private}
                    onChange={(e) => setNewNote({...newNote, is_private: e.target.checked})}
                  />
                  🔒 Private note (only you can see this)
                </label>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowNoteForm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateNote}
                disabled={!newNote.title || !newNote.content || !newNote.course_name}
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
